import { apiSlice } from "@/app/apiSlice";
import { API_ENDPOINTS } from "@/constants/apiEndpoints";
import { blobDownloadQuery } from "@/features/financialShared";
import type {
	CreateRevenueServicePeriodBody,
	ExportRevenueServicePeriodUnitsArgs,
	GetRevenueManagementPropertiesArgs,
	GetRevenueServicePeriodsArgs,
	GetRevenueServicePeriodUnitsArgs,
	RevenueManagementPropertiesResponse,
	RevenueServicePeriodsResponse,
	RevenueServicePeriodUnitsResponse,
	RevenueUnitPathArgs,
	UpdateRevenueUnitStatementArgs,
	UploadRevenueServicePeriodUnitsArgs,
} from "./revenueManagement.api.types";
import {
	buildRevenueManagementListParams,
	buildRevenueServicePeriodListParams,
	parseRevenueManagementPropertiesResponse,
	parseRevenueServicePeriodsResponse,
} from "./revenueManagement.mappers";
import type { RevenueUnitDetail } from "./revenueManagement.types";
import {
	buildRevenueServicePeriodUnitsParams,
	parseRevenueServicePeriodUnitsResponse,
	parseRevenueUnitStatement,
} from "./revenueManagement.unitMappers";

function unitsTag(propertyId: string, periodId: string) {
	return {
		type: "RevenueServicePeriodUnits" as const,
		id: `${propertyId}:${periodId}`,
	};
}

export const revenueManagementApi = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getRevenueManagementProperties: builder.query<
			RevenueManagementPropertiesResponse,
			GetRevenueManagementPropertiesArgs | undefined
		>({
			query: (args) => ({
				url: API_ENDPOINTS.properties.revenueManagement,
				params: buildRevenueManagementListParams(args ?? {}),
			}),
			transformResponse: (raw: unknown) =>
				parseRevenueManagementPropertiesResponse(raw),
			providesTags: [{ type: "RevenueManagementProperties", id: "LIST" }],
		}),
		getRevenueServicePeriods: builder.query<
			RevenueServicePeriodsResponse,
			GetRevenueServicePeriodsArgs
		>({
			query: (args) => ({
				url: API_ENDPOINTS.properties.revenueServicePeriods(args.propertyId),
				params: buildRevenueServicePeriodListParams(args),
			}),
			transformResponse: (raw: unknown, _meta, args) =>
				parseRevenueServicePeriodsResponse(raw, args.propertyId),
			providesTags: (_result, _error, args) => [
				{ type: "RevenueServicePeriods", id: args.propertyId },
			],
		}),
		createRevenueServicePeriod: builder.mutation<
			unknown,
			{ propertyId: string; body: CreateRevenueServicePeriodBody }
		>({
			query: ({ propertyId, body }) => ({
				url: API_ENDPOINTS.properties.revenueServicePeriods(propertyId),
				method: "POST",
				body,
			}),
			invalidatesTags: (_result, _error, arg) => [
				{ type: "RevenueServicePeriods", id: arg.propertyId },
				{ type: "RevenueManagementProperties", id: "LIST" },
			],
		}),
		publishRevenueServicePeriod: builder.mutation<
			unknown,
			{ propertyId: string; periodId: string }
		>({
			query: ({ propertyId, periodId }) => ({
				url: API_ENDPOINTS.properties.publishRevenueServicePeriod(
					propertyId,
					periodId,
				),
				method: "POST",
			}),
			invalidatesTags: (_result, _error, arg) => [
				{ type: "RevenueServicePeriods", id: arg.propertyId },
				{ type: "RevenueManagementProperties", id: "LIST" },
			],
		}),
		downloadRevenueSampleXls: builder.mutation<DownloadedFile, void>({
			query: () =>
				blobDownloadQuery(
					API_ENDPOINTS.properties.revenueServicePeriodSampleXls,
					"revenue-service-period-sample.xls",
				),
		}),
		getRevenueServicePeriodUnits: builder.query<
			RevenueServicePeriodUnitsResponse,
			GetRevenueServicePeriodUnitsArgs
		>({
			query: (args) => ({
				url: API_ENDPOINTS.properties.revenueServicePeriodUnits(
					args.propertyId,
					args.periodId,
				),
				params: buildRevenueServicePeriodUnitsParams(args),
			}),
			transformResponse: (raw: unknown, _meta, args) =>
				parseRevenueServicePeriodUnitsResponse(raw, {
					propertyId: args.propertyId,
					periodId: args.periodId,
				}),
			providesTags: (_result, _error, args) => [
				unitsTag(args.propertyId, args.periodId),
			],
		}),
		exportRevenueServicePeriodUnits: builder.mutation<
			DownloadedFile,
			ExportRevenueServicePeriodUnitsArgs
		>({
			query: ({ propertyId, periodId, scope }) =>
				blobDownloadQuery(
					API_ENDPOINTS.properties.exportRevenueServicePeriodUnits(
						propertyId,
						periodId,
					),
					scope === "PENDING"
						? "revenue-units-pending.xls"
						: "revenue-units-all.xls",
					{ scope },
				),
		}),
		uploadRevenueServicePeriodUnits: builder.mutation<
			unknown,
			UploadRevenueServicePeriodUnitsArgs
		>({
			query: ({ propertyId, periodId, body }) => ({
				url: API_ENDPOINTS.properties.uploadRevenueServicePeriodUnits(
					propertyId,
					periodId,
				),
				method: "POST",
				body,
			}),
			invalidatesTags: (_result, _error, arg) => [
				unitsTag(arg.propertyId, arg.periodId),
				{ type: "RevenueServicePeriods", id: arg.propertyId },
				{ type: "RevenueManagementProperties", id: "LIST" },
				{ type: "RevenueUnitStatement", id: "LIST" },
			],
		}),
		getRevenueUnitStatement: builder.query<
			RevenueUnitDetail,
			RevenueUnitPathArgs
		>({
			query: ({ propertyId, periodId, unitId }) => ({
				url: API_ENDPOINTS.properties.revenueServicePeriodUnit(
					propertyId,
					periodId,
					unitId,
				),
			}),
			transformResponse: (raw: unknown, _meta, args) =>
				parseRevenueUnitStatement(raw, args),
			providesTags: (_result, _error, args) => [
				{ type: "RevenueUnitStatement", id: args.unitId },
				{ type: "RevenueUnitStatement", id: "LIST" },
			],
		}),
		updateRevenueUnitStatement: builder.mutation<
			unknown,
			UpdateRevenueUnitStatementArgs
		>({
			query: ({ propertyId, periodId, unitId, body }) => ({
				url: API_ENDPOINTS.properties.revenueServicePeriodUnit(
					propertyId,
					periodId,
					unitId,
				),
				method: "PATCH",
				body,
			}),
			invalidatesTags: (_result, _error, arg) => [
				{ type: "RevenueUnitStatement", id: arg.unitId },
				unitsTag(arg.propertyId, arg.periodId),
			],
		}),
		exportRevenueUnitCsv: builder.mutation<DownloadedFile, RevenueUnitPathArgs>(
			{
				query: ({ propertyId, periodId, unitId }) =>
					blobDownloadQuery(
						API_ENDPOINTS.properties.exportRevenueServicePeriodUnit(
							propertyId,
							periodId,
							unitId,
						),
						"revenue-unit-statement.csv",
					),
			},
		),
		downloadRevenueUnitStatementPdf: builder.mutation<
			DownloadedFile,
			RevenueUnitPathArgs
		>({
			query: ({ propertyId, periodId, unitId }) =>
				blobDownloadQuery(
					API_ENDPOINTS.properties.revenueServicePeriodUnitStatement(
						propertyId,
						periodId,
						unitId,
					),
					"owner-statement.pdf",
				),
		}),
	}),
	overrideExisting: false,
});

export const {
	useGetRevenueManagementPropertiesQuery,
	useGetRevenueServicePeriodsQuery,
	useCreateRevenueServicePeriodMutation,
	usePublishRevenueServicePeriodMutation,
	useDownloadRevenueSampleXlsMutation,
	useGetRevenueServicePeriodUnitsQuery,
	useExportRevenueServicePeriodUnitsMutation,
	useUploadRevenueServicePeriodUnitsMutation,
	useGetRevenueUnitStatementQuery,
	useUpdateRevenueUnitStatementMutation,
	useExportRevenueUnitCsvMutation,
	useDownloadRevenueUnitStatementPdfMutation,
} = revenueManagementApi;
